import React, { useState, useEffect, useCallback } from 'react';
import axios from '../config/axios';

/**
 * QuestProgress - Shows a kid's weekly quest progress
 */
const QuestProgress = ({ kidUserId }) => {
    const [quests, setQuests] = useState([]);
    const [loading, setLoading] = useState(true);

    const fetchQuests = useCallback(async () => {
        try {
            const response = await axios.get(`/api/v1/quests?kid_id=${kidUserId}`);
            // Ensure quests is always an array
            const data = response.data;
            setQuests(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error('Failed to fetch quests:', err);
            setQuests([]);
        } finally {
            setLoading(false);
        }
    }, [kidUserId]);

    useEffect(() => {
        fetchQuests();
        // Refresh every 30 seconds
        const interval = setInterval(fetchQuests, 30000);
        return () => clearInterval(interval);
    }, [fetchQuests]);

    if (loading) {
        return <div style={{ padding: '16px', color: '#9ca3af' }}>Loading quests...</div>;
    }

    if (!Array.isArray(quests) || quests.length === 0) {
        return null; // Don't show section if no quests
    }

    return (
        <div style={{
            background: 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)',
            borderRadius: '20px',
            padding: '24px',
            marginBottom: '24px',
            color: 'white',
            boxShadow: '0 8px 16px rgba(139, 92, 246, 0.3)'
        }}>
            <h2 style={{
                fontSize: '1.5rem',
                marginBottom: '20px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
            }}>
                🎯 Weekly Quests
            </h2>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {quests.map(quest => {
                    // Find this kid's progress
                    const myProgress = quest.assignees?.find(a => a.kid_user_id === kidUserId);
                    if (!myProgress) return null;

                    const percent = myProgress.percent_complete || 0;
                    const isComplete = myProgress.is_complete;

                    return (
                        <div key={quest.ID} style={{
                            background: isComplete ? 'rgba(16, 185, 129, 0.2)' : 'rgba(255, 255, 255, 0.1)',
                            borderRadius: '16px',
                            padding: '20px',
                            border: isComplete ? '2px solid #10b981' : '1px solid rgba(255,255,255,0.2)'
                        }}>
                            <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                marginBottom: '12px'
                            }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                    <span style={{ fontSize: '32px' }}>{quest.emoji || '🎯'}</span>
                                    <div>
                                        <h3 style={{ margin: 0, fontSize: '1.1rem' }}>
                                            {quest.title}
                                            {isComplete && <span style={{ marginLeft: '8px' }}>✅</span>}
                                        </h3>
                                        {quest.description && (
                                            <p style={{ margin: '4px 0 0', opacity: 0.8, fontSize: '0.9rem' }}>
                                                {quest.description}
                                            </p>
                                        )}
                                    </div>
                                </div>
                                <div style={{ textAlign: 'right' }}>
                                    <div style={{
                                        fontSize: '1.5rem',
                                        fontWeight: 'bold',
                                        color: isComplete ? '#10b981' : '#fbbf24'
                                    }}>
                                        ${(quest.bonus_reward / 100).toFixed(2)}
                                    </div>
                                    <div style={{ fontSize: '0.8rem', opacity: 0.8 }}>bonus</div>
                                </div>
                            </div>

                            {/* Progress bar */}
                            <div style={{
                                background: 'rgba(0, 0, 0, 0.2)',
                                borderRadius: '8px',
                                height: '16px',
                                overflow: 'hidden',
                                marginBottom: '8px'
                            }}>
                                <div style={{
                                    background: isComplete
                                        ? 'linear-gradient(90deg, #10b981, #059669)'
                                        : 'linear-gradient(90deg, #fbbf24, #f59e0b)',
                                    width: `${percent}%`,
                                    height: '100%',
                                    borderRadius: '8px',
                                    transition: 'width 0.5s ease'
                                }} />
                            </div>

                            <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                fontSize: '0.9rem',
                                opacity: 0.9
                            }}>
                                <span>
                                    {myProgress.current_progress} / {myProgress.target_value}
                                    {quest.target_type === 'chores_completed' && ' chores'}
                                    {quest.target_type === 'total_earned' && ' DadBucks'}
                                    {quest.target_type === 'streak_days' && ' days'}
                                </span>
                                <span>{percent}% complete</span>
                            </div>

                            {isComplete && myProgress.bonus_paid && (
                                <div style={{
                                    marginTop: '12px',
                                    padding: '8px 12px',
                                    background: 'rgba(16, 185, 129, 0.3)',
                                    borderRadius: '8px',
                                    textAlign: 'center',
                                    fontWeight: '600'
                                }}>
                                    🎉 Bonus Paid! +${(quest.bonus_reward / 100).toFixed(2)}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default QuestProgress;
